/**
 * Klaviyo "Newsletter Signup" event for the signup offer (Theme settings >
 * Newsletter signup offer). While the offer is on, every Shopify newsletter
 * form (form_type "customer") remembers the email on submit, and the event is
 * sent only once Shopify has accepted the signup (after any hCaptcha): the
 * form then renders [data-signup-posted] from form.posted_successfully?, so a
 * Klaviyo flow can email the coupon. Reloads and Back don't resend.
 * Covers new and existing subscribers alike.
 * Exposes window.klaviyoSignup.track(email, source) for the contact form.
 */
(() => {
  const API_REVISION = '2025-01-15';
  const STORAGE_KEY = 'klaviyoSignupPending';
  const MAX_AGE = 30 * 60 * 1000;
  const script = document.currentScript;
  const offerEnabled = !!script && script.dataset.offerEnabled === 'true';
  const offerPercent = (script && Number(script.dataset.offerPercent)) || 0;

  const getCompanyId = () => {
    const klaviyoScript = document.querySelector('script[src*="klaviyo.js"]');
    if (!klaviyoScript) return '';
    const match = klaviyoScript.src.match(/[?&]company_id=([A-Za-z0-9]+)/);
    return match ? match[1] : '';
  };

  const track = (email, source) => {
    const companyId = getCompanyId();
    if (!offerEnabled || !companyId || !email) return Promise.resolve();
    return fetch(`https://a.klaviyo.com/client/events?company_id=${companyId}`, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/vnd.api+json', revision: API_REVISION },
      body: JSON.stringify({
        data: {
          type: 'event',
          attributes: {
            metric: { data: { type: 'metric', attributes: { name: 'Newsletter Signup' } } },
            profile: { data: { type: 'profile', attributes: { email } } },
            properties: {
              source,
              offer_enabled: true,
              offer_percent: offerPercent,
              consent: true,
              page_url: window.location.href,
            },
            time: new Date().toISOString(),
          },
        },
      }),
    }).then((response) => {
      if (!response.ok) {
        response.text().then((body) => console.warn(`Klaviyo Newsletter Signup ${response.status}:`, body));
      }
      return response;
    });
  };

  const getSource = (form) => {
    if (form.id && form.id !== 'contact_form') return form.id;
    if (document.body.classList.contains('template-password')) return 'password';
    if (form.closest('.shopify-section-group-footer-group, .shopify-section-footer')) return 'footer';
    if (form.closest('.shopify-section-group-header-group')) return 'header';
    const section = form.closest('[id^="shopify-section-"]');
    if (!section) return 'newsletter';
    const key = section.id.replace('shopify-section-', '').replace(/^(template|sections)--\d+__/, '');
    return key && !/^[0-9a-f-]+$/i.test(key) ? key : 'newsletter';
  };

  const remember = (form) => {
    const emailField = form.querySelector('input[type="email"]');
    const email = emailField ? emailField.value.trim() : '';
    if (!email) return;
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ email, source: getSource(form), formId: form.id, at: Date.now() }));
    } catch (error) {}
  };

  const isFreshNavigation = () => {
    const entry = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
    return !entry || entry.type === 'navigate';
  };

  const sendIfPosted = () => {
    if (!document.querySelector('[data-signup-posted]') || !isFreshNavigation()) return;
    let signup = null;
    try {
      signup = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {}
    if (!signup || !signup.email || Date.now() - signup.at > MAX_AGE) return;
    const form = signup.formId && document.getElementById(signup.formId);
    if (form && !form.querySelector('[data-signup-posted]')) return;
    track(signup.email, signup.source).catch((error) => console.error('Klaviyo newsletter signup tracking failed:', error));
  };

  const initAll = () => {
    if (!offerEnabled) return;
    document.querySelectorAll('input[name="form_type"][value="customer"]').forEach((input) => {
      if (input.form) input.form.addEventListener('submit', () => remember(input.form), true);
    });
    sendIfPosted();
  };

  window.klaviyoSignup = { offerEnabled, offerPercent, track };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
